import { useEffect, useState, useRef } from 'react';
import { membersAPI, notificationsAPI } from '../../services/api';

const ITEMS_PER_PAGE = 25;
const SYNC_LIMIT = 1000; // above this, queue as a background job instead of sending inline
const DEFAULT_MESSAGE = 'Habari, hii ni taarifa kutoka AWTF. Ingia hapa: https://awtf.or.tz — Asante.';

export default function Notifications() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState({}); // { [memberId]: { id, full_name } }
  const [allFilterCount, setAllFilterCount] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    membersAPI.statuses().then((r) => setStatuses(r.statuses || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    membersAPI.list(page, ITEMS_PER_PAGE, query, status)
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.response?.data?.error || 'Failed to load members.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, query, status]);

  // Filter changed — the "select all matching" count is stale, clear it.
  useEffect(() => { setAllFilterCount(null); }, [query, status]);

  const members = data?.members || [];
  const pageAllSelected = members.length > 0 && members.every((m) => selected[m.id]);
  const selectedList = Object.values(selected);

  function toggleOne(member) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[member.id]) delete next[member.id]; else next[member.id] = { id: member.id, full_name: member.full_name };
      return next;
    });
  }
  function togglePage() {
    setSelected((prev) => {
      const next = { ...prev };
      if (pageAllSelected) { members.forEach((m) => delete next[m.id]); }
      else { members.forEach((m) => { next[m.id] = { id: m.id, full_name: m.full_name }; }); }
      return next;
    });
  }
  function clearSelection() { setSelected({}); }

  async function selectAllMatching() {
    setSelectingAll(true);
    try {
      const resp = await membersAPI.allIds(query, status);
      setSelected((prev) => {
        const next = { ...prev };
        (resp.ids || []).forEach((id) => { if (!next[id]) next[id] = { id, full_name: null }; });
        return next;
      });
      setAllFilterCount(resp.total);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to select all matching members.');
    } finally {
      setSelectingAll(false);
    }
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Notifications</h2>
        <p className="mt-1 text-sm text-slate-600">Select members below, then write a message to send by SMS and/or email.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input aria-label="Search members" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, roll number or email…"
          className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
        <select aria-label="Status filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" disabled={selectedList.length === 0} onClick={() => setComposeOpen(true)}
          className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-40">
          📣 Notify {selectedList.length > 0 ? `(${selectedList.length})` : ''}
        </button>
      </div>

      {selectedList.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-xs text-orange-800">
          <span className="font-semibold">{selectedList.length.toLocaleString()} selected</span>
          {allFilterCount === null && data?.total > members.length && (
            <button type="button" onClick={selectAllMatching} disabled={selectingAll}
              className="rounded-lg border border-orange-300 bg-white px-2.5 py-1 font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-50">
              {selectingAll ? 'Selecting…' : `Select all ${data.total.toLocaleString()} matching this filter`}
            </button>
          )}
          <button type="button" onClick={clearSelection} className="ml-auto text-orange-600 hover:text-orange-800 underline">Clear selection</button>
        </div>
      )}

      {loading ? <p role="status" className="py-8 text-center text-slate-500">Loading members…</p> : error ? <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">{error}</p> : <>
        <p className="text-sm text-slate-600">{data?.total?.toLocaleString() || 0} members match this filter</p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="whitespace-nowrap px-4 py-3"><input type="checkbox" checked={pageAllSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
              {['Member', 'Roll No.', 'Status', 'Phone', 'Email'].map((label) => <th key={label} className="whitespace-nowrap px-4 py-3 font-semibold">{label}</th>)}
            </tr></thead>
            <tbody>{members.map((member) => (
              <tr key={member.id} className={`border-t border-slate-100 ${selected[member.id] ? 'bg-orange-50/50' : ''}`}>
                <td className="px-4 py-3"><input type="checkbox" checked={!!selected[member.id]} onChange={() => toggleOne(member)} aria-label={`Select ${member.full_name}`} /></td>
                <td className="min-w-48 px-4 py-3 font-medium text-slate-800">{member.full_name}</td>
                <td className="px-4 py-3">{member.roll_no || '—'}</td>
                <td className="whitespace-nowrap px-4 py-3">{member.status || '—'}</td>
                <td className="whitespace-nowrap px-4 py-3">{member.phone || '—'}</td>
                <td className="px-4 py-3">{member.email || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
          {!members.length && <p className="p-8 text-center text-slate-500">No members match this filter.</p>}
        </div>
        <div className="flex items-center justify-between gap-2 text-sm">
          <span>Page {data?.page || 1} of {data?.total_pages || 1}</span>
          <div className="flex gap-2">
            <button type="button" disabled={!data?.has_previous} onClick={() => setPage((p) => p - 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Previous</button>
            <button type="button" disabled={!data?.has_next} onClick={() => setPage((p) => p + 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Next</button>
          </div>
        </div>
      </>}

      {composeOpen && (
        <ComposeModal
          recipients={selectedList}
          onClose={() => setComposeOpen(false)}
          onSent={() => { clearSelection(); setComposeOpen(false); }}
        />
      )}
    </section>
  );
}

function ComposeModal({ recipients, onClose, onSent }) {
  const [channel, setChannel] = useState('sms');
  const [subject, setSubject] = useState('AWTF Notification');
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // sync result
  const [job, setJob] = useState(null); // async job status
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  async function handleSend() {
    if (!message.trim()) { setError('Message text is required.'); return; }
    setSending(true); setError('');
    const ids = recipients.map((m) => m.id);
    try {
      if (ids.length > SYNC_LIMIT) {
        const resp = await notificationsAPI.queue(ids, channel, message.trim(), subject.trim());
        setJob({ id: resp.job_id, total: resp.total, processed: 0, sent: 0, failed: 0, status: 'pending' });
        pollRef.current = setInterval(async () => {
          try {
            const r = await notificationsAPI.jobStatus(resp.job_id);
            setJob(r.job);
            if (r.job.status === 'completed') clearInterval(pollRef.current);
          } catch { /* keep polling on transient errors */ }
        }, 3000);
      } else {
        const resp = await notificationsAPI.send(ids, channel, message.trim(), subject.trim());
        setResult(resp);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send notifications.');
      setSending(false);
    }
  }

  const noPhoneCount = channel !== 'email' ? recipients.filter((m) => m.full_name && !m.phone).length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Notify {recipients.length.toLocaleString()} member{recipients.length === 1 ? '' : 's'}</h3>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
        </div>

        {job ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-700">{job.status === 'completed' ? 'Done' : 'Sending…'}</span>
                <span className="text-slate-500">{job.processed.toLocaleString()} / {job.total.toLocaleString()}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">✓ {job.sent.toLocaleString()} sent · ✕ {job.failed.toLocaleString()} failed</p>
              {job.status !== 'completed' && <p className="mt-1 text-[11px] text-slate-400">This runs in the background — you can close this and check back later.</p>}
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onSent} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
                {job.status === 'completed' ? 'Done' : 'Close (keeps sending)'}
              </button>
            </div>
          </div>
        ) : result ? (
          <div className="mt-4 space-y-3">
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{result.message}</p>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-2">Member</th>{channel !== 'email' && <th className="px-3 py-2">SMS</th>}{channel !== 'sms' && <th className="px-3 py-2">Email</th>}</tr></thead>
                <tbody>{result.results.map((r) => (
                  <tr key={r.member_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{r.full_name}</td>
                    {channel !== 'email' && <td className={`px-3 py-2 ${r.sms === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.sms || '—'}</td>}
                    {channel !== 'sms' && <td className={`px-3 py-2 ${r.email === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.email || '—'}</td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onSent} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">Done</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Channel</label>
              <div className="flex gap-2">
                {[['sms', 'SMS'], ['email', 'Email'], ['both', 'SMS + Email']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setChannel(key)}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold ${channel === key ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {noPhoneCount > 0 && <p className="mt-1.5 text-[11px] text-amber-600">{noPhoneCount} of the members shown on this page have no phone on file.</p>}
            </div>

            {channel !== 'sms' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Email subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Message</label>
              <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>

            {recipients.length > SYNC_LIMIT && (
              <p className="text-[11px] text-slate-500">This is a large group ({recipients.length.toLocaleString()}) — it will be sent gradually in the background so it doesn't overload the SMS/email gateway. You'll see live progress here.</p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} disabled={sending}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                Cancel
              </button>
              <button type="button" onClick={handleSend} disabled={sending}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold">
                {sending ? 'Sending…' : `Send to ${recipients.length.toLocaleString()}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
